import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Card, Col, Empty, Input, Row, Typography } from "antd";
import {
    SettingOutlined, SearchOutlined, RightOutlined,
    SolutionOutlined, EnvironmentOutlined, BankOutlined, PushpinOutlined,
    CalendarOutlined, SafetyCertificateOutlined, PieChartOutlined, ThunderboltOutlined,
    TeamOutlined, DeleteOutlined, AppstoreOutlined, HistoryOutlined,
} from "@ant-design/icons";
import Footer from "../../components/Footer";
import NavBar from "../../components/NavBar";
import SideBar from "../../components/SideBar";

const { Text, Title } = Typography;
const PRIMARY = "#242166";

// Paramètres généraux regroupés par thème (style aligné sur /parametres/sinistres & /tarification/v2).
// Les liens de destination sont STRICTEMENT identiques à l'ancien hub /autres-parametres.
const GROUPS = [
    {
        key: "referentiels",
        label: "Référentiels métier",
        items: [
            { title: "Professions", description: "Professions utilisées dans les fiches personnes et l'affectation des experts.", link: "/autre-parametres/professions", button: "Définir une profession", icon: <SolutionOutlined />, color: "#2563eb" },
            { title: "Villes", description: "Villes de référence pour les adresses et agences.", link: "/autre-parametres/villes", button: "Définir une ville", icon: <EnvironmentOutlined />, color: "#0891b2" },
            { title: "Lieux", description: "Lieux de survenance utilisés dans les dossiers sinistres.", link: "/autre-parametres/lieux", button: "Définir un lieu", icon: <PushpinOutlined />, color: "#dc2626" },
            { title: "Banques", description: "Banques utilisées pour les règlements et encaissements.", link: "/autre-parametres/banques", button: "Définir une banque", icon: <BankOutlined />, color: "#7c3aed" },
        ],
    },
    {
        key: "production",
        label: "Production & comptabilité",
        items: [
            { title: "Exercices", description: "Exercices comptables (année d'inventaire) ouverts dans l'application.", link: "/autre-parametres/exercices", button: "Définir un exercice", icon: <CalendarOutlined />, color: "#d97706" },
            { title: "Produits d'attestation", description: "Produits relatifs aux attestations (lots, gammes).", link: "/autre-parametres/produits-attestation", button: "Définir un produit", icon: <SafetyCertificateOutlined />, color: "#16a34a" },
            { title: "Clé de répartition", description: "Clés de répartition du SAP entre compagnies, par branche et exercice.", link: "/autre-parametres/cle-de-repartition/v2", button: "Définir la clé", icon: <PieChartOutlined />, color: PRIMARY },
            { title: "Pénalités de cession", description: "Pénalités appliquées sur les cessions.", link: "/parametres/penalite-cession", button: "Définir les pénalités", icon: <ThunderboltOutlined />, color: "#db2777" },
            { title: "Cession des mois antérieurs", description: "Autoriser (ou non) la proposition des émissions/décaissements des mois antérieurs dans l'écran de cession. Désactivé par défaut.", link: "/autre-parametres/cession-anterieurs", button: "Configurer", icon: <HistoryOutlined />, color: "#0e7490" },
            { title: "Motifs d'exclusion (rétrocession)", description: "Motifs pour écarter une ligne (émission / annulation / sinistre / recours) de la rétrocession.", link: "/autre-parametres/motifs-exclusion-retrocession", button: "Définir les motifs", icon: <ThunderboltOutlined />, color: "#e11d48" },
        ],
    },
    {
        key: "systeme",
        label: "Configuration système",
        items: [
            { title: "Types de profils", description: "Types de profils utilisateurs et leurs rôles.", link: "/autre-parametres/type-profils", button: "Définir un type de profil", icon: <TeamOutlined />, color: "#0d9488" },
            { title: "Délai de mise en corbeille", description: "Durée avant mise en corbeille automatique des propositions.", link: "/autre-parametres/delai-corbeille", button: "Définir la durée", icon: <DeleteOutlined />, color: "#ca8a04" },
            { title: "Autres paramètres", description: "Paramètres divers non catégorisés de l'application.", link: "/autre-parametres/autre-parametre", button: "Définir un paramètre", icon: <AppstoreOutlined />, color: "#4f46e5" },
        ],
    },
];

const AutresParametresV2 = () => {
    const navigate = useNavigate();
    const [search, setSearch] = useState("");

    const filteredGroups = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return GROUPS;
        return GROUPS
            .map((g) => ({ ...g, items: g.items.filter((i) => `${i.title} ${i.description}`.toLowerCase().includes(q)) }))
            .filter((g) => g.items.length);
    }, [search]);

    return (
        <div className="layout-wrapper layout-content-navbar">
            <div className="layout-container">
                <SideBar currentMenu={"AUTRES_PARAMETRES"} />
                <div className="layout-page">
                    <NavBar />
                    <div className="content-wrapper">
                        <div className="container-xxl flex-grow-1 container-p-y">

                            {/* En-tête */}
                            <div style={{ background: `linear-gradient(135deg, ${PRIMARY} 0%, #1890ff 100%)`, borderRadius: 14, padding: "18px 22px", marginBottom: 20, color: "#fff", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
                                <div>
                                    <Title level={4} style={{ margin: 0, color: "#fff" }}>
                                        <SettingOutlined style={{ marginRight: 8 }} />Autres paramètres
                                    </Title>
                                    <Text style={{ color: "rgba(255,255,255,0.85)" }}>
                                        Référentiels métier, production &amp; comptabilité et configuration système
                                    </Text>
                                </div>
                                <Input
                                    allowClear
                                    prefix={<SearchOutlined style={{ color: "rgba(255,255,255,0.7)" }} />}
                                    placeholder="Rechercher un paramètre…"
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                    style={{ maxWidth: 320, background: "rgba(255,255,255,0.15)", border: "1px solid rgba(255,255,255,0.3)", borderRadius: 8 }}
                                    styles={{ input: { background: "transparent", color: "#fff" } }}
                                />
                            </div>

                            {/* Groupes de cartes */}
                            {filteredGroups.length === 0 ? (
                                <Empty description="Aucun paramètre trouvé" style={{ padding: "50px 0" }} />
                            ) : filteredGroups.map((g) => (
                                <div key={g.key} style={{ marginBottom: 22 }}>
                                    <div style={{ fontSize: 12, fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 10 }}>{g.label}</div>
                                    <Row gutter={[16, 16]}>
                                        {g.items.map((item) => (
                                            <Col xs={24} sm={12} lg={8} xxl={6} key={item.title}>
                                                <Card hoverable bordered={false} onClick={() => navigate(item.link)}
                                                    style={{ borderRadius: 14, boxShadow: "0 2px 10px rgba(0,0,0,0.06)", height: "100%" }} bodyStyle={{ padding: 18, display: "flex", flexDirection: "column", height: "100%" }}>
                                                    <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 10 }}>
                                                        <span style={{ width: 42, height: 42, borderRadius: 11, background: `${item.color}14`, color: item.color, display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 20, flexShrink: 0 }}>{item.icon}</span>
                                                        <div style={{ fontWeight: 700, color: "#111827", fontSize: 15 }}>{item.title}</div>
                                                    </div>
                                                    <Text type="secondary" style={{ fontSize: 13, flex: 1 }}>{item.description}</Text>
                                                    <div style={{ marginTop: 14 }}>
                                                        <Button type="link" style={{ padding: 0, color: item.color, fontWeight: 600 }}>{item.button} <RightOutlined /></Button>
                                                    </div>
                                                </Card>
                                            </Col>
                                        ))}
                                    </Row>
                                </div>
                            ))}
                        </div>
                        <Footer />
                        <div className="content-backdrop fade"></div>
                    </div>
                </div>
            </div>
            <div className="layout-overlay layout-menu-toggle"></div>
            <div className="drag-target"></div>
        </div>
    );
};

export default AutresParametresV2;
